/**
 * Content Stage — Stage 3: Content quality validation.
 *
 * ARCHITECTURE v4.0 — Phase 2, Step 2.4
 *
 * Validates:
 * - Explanation quality (not generic, has didactic depth)
 * - Distractor explanations (present, not generic, reference misconceptions)
 * - Forbidden vocabulary per tier
 * - Question text length per tier
 * - Module-specific rules (from governance validator)
 * - Notation requirements (LaTeX, comma decimals, units)
 */

import type { ValidationIssue, PipelineStage } from '../issueContract'
import { blocker, coach, info } from '../issueContract'
import {
  getPolicy,
  getTierBand,
  getForbiddenVocab,
  getMaxQuestionLength,
  getMinExplanationLength,
  getMinDistractorLength,
  getNotationRequirements,
  getDistractorPattern,
  requiresRuleReference,
  getCheckStrategyMinTier,
  isPolicyFeatureEnabled,
  isPlusTier,
  getPlusRequirements,
  isAssessmentEligibleType,
} from '../policyEngine'
import {
  isGenericText,
  isDidactic,
  GENERIC_EXPLANATION_PATTERNS,
  GENERIC_DISTRACTOR_PATTERNS,
  FORBIDDEN_OPENER_PATTERNS,
  detectJargon,
  hasPassiveVoice,
  detectLongSentences,
  getMaxWordsPerSentence,
} from '../../utils'
import { detectCognitiveLevel } from '../../cognitiveLevel'
import type { Question, ModuleId } from '../../../content'

const STAGE: PipelineStage = 3

/**
 * Run content quality validation on a question.
 */
export function runContentStage(question: Question): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? 'UNKNOWN'
  const tier = question.tier ?? 1
  const module = question.module

  // ── Forbidden vocabulary ─────────────────────────────────────────────────

  const forbidden = getForbiddenVocab(tier)
  const questionText = (question.question ?? '').toLowerCase()
  for (const word of forbidden) {
    if (questionText.includes(word.toLowerCase())) {
      issues.push(
        coach(
          'content',
          STAGE,
          'CONTENT_FORBIDDEN_VOCAB',
          `Word "${word}" too complex for tier ${tier} (${getTierBand(tier)} band)`,
          {
            field: 'question',
            actual: word,
            questionId: qId,
            familyId: fId,
            severity: 40,
            suggestion: `Use simpler vocabulary appropriate for tier ${tier}`,
          },
        ),
      )
    }
  }

  // ── NEW: Jargon detection in question text (B2 — was only in explanation) ─
  {
    const qJargon = detectJargon(question.question ?? '', tier)
    for (const { term, replacement } of qJargon) {
      issues.push(
        coach(
          'content',
          STAGE,
          'CONTENT_JARGON_DETECTED',
          `Jargon "${term}" in question text — too complex for children aged 9-11`,
          {
            field: 'question',
            actual: term,
            questionId: qId,
            familyId: fId,
            severity: 40,
            suggestion: `Replace "${term}" with ${replacement}`,
          },
        ),
      )
    }
  }

  // ── NEW: Sentence length in question text (B2 — was only in explanation) ─
  {
    const qLongSentences = detectLongSentences(question.question ?? '', tier)
    if (qLongSentences.length > 0) {
      const maxWords = getMaxWordsPerSentence(tier)
      issues.push(
        info(
          'content',
          STAGE,
          'CONTENT_QUESTION_SENTENCE_TOO_LONG',
          `${qLongSentences.length} sentence(s) in question text exceed the ${maxWords}-word limit for tier ${tier}`,
          {
            field: 'question',
            questionId: qId,
            familyId: fId,
            severity: 20,
            suggestion: `Keep question sentences ≤${maxWords} words for tier ${tier} (groep ${tier <= 4 ? 6 : tier <= 8 ? 7 : 8})`,
          },
        ),
      )
    }
  }

  // ── Question text length ─────────────────────────────────────────────────

  const maxLength = getMaxQuestionLength(tier, module)
  if (questionText.length > maxLength) {
    issues.push(
      coach(
        'content',
        STAGE,
        'CONTENT_QUESTION_TOO_LONG',
        `Question text (${questionText.length} chars) exceeds max ${maxLength} for tier ${tier}`,
        {
          field: 'question',
          expected: `≤${maxLength}`,
          actual: String(questionText.length),
          questionId: qId,
          familyId: fId,
          severity: 35,
        },
      ),
    )
  }

  // ── GAP-P3: Context field length validation ──────────────────────────────
  // For TAA/STU modules, the `context` field may contain long reading passages.
  // Validate that context doesn't exceed reasonable limits per tier band.

  const contextText = (question as Record<string, unknown>).context
  if (
    contextText &&
    typeof contextText === 'string' &&
    contextText.length > 0
  ) {
    const band = getTierBand(tier)
    const maxContextLength: Record<string, number> = {
      basic: 800,
      intermediate: 1200,
      advanced: 2000,
      expert: 3000,
    }
    const maxCtx = maxContextLength[band] ?? 2000
    if (contextText.length > maxCtx) {
      issues.push(
        info(
          'content',
          STAGE,
          'CONTENT_CONTEXT_TOO_LONG',
          `Context field (${contextText.length} chars) exceeds recommended max ${maxCtx} for tier ${tier} (${band} band)`,
          {
            field: 'context',
            expected: `≤${maxCtx}`,
            actual: String(contextText.length),
            questionId: qId,
            familyId: fId,
            severity: 15,
          },
        ),
      )
    }
  }

  // ── Explanation quality ──────────────────────────────────────────────────

  const explanation = question.feedback?.explanationText?.trim() ?? ''
  if (explanation) {
    // Min length check
    const minExplanation = getMinExplanationLength(tier)
    if (explanation.length < minExplanation) {
      issues.push(
        coach(
          'content',
          STAGE,
          'CONTENT_EXPLANATION_TOO_SHORT',
          `Explanation (${explanation.length} chars) below minimum ${minExplanation} for tier ${tier}`,
          {
            field: 'feedback.explanationText',
            expected: `≥${minExplanation}`,
            actual: String(explanation.length),
            questionId: qId,
            familyId: fId,
            severity: 50,
          },
        ),
      )
    }

    // Generic check
    if (isGenericText(explanation, GENERIC_EXPLANATION_PATTERNS)) {
      issues.push(
        coach(
          'content',
          STAGE,
          'CONTENT_GENERIC_EXPLANATION',
          'Explanation is generic/boilerplate — must explain the concept or calculation',
          {
            field: 'feedback.explanationText',
            questionId: qId,
            familyId: fId,
            severity: 55,
            suggestion:
              'Add step-by-step reasoning, reference the rule, or show the calculation',
          },
        ),
      )
    }

    // ── Forbidden opener check (CAL-006 fix) ─────────────────────────────
    // Independent of text length/substance — flags any explanation that
    // starts with a forbidden opener pattern, even if the rest is didactic.
    // Uses shared FORBIDDEN_OPENER_PATTERNS from governance/utils.ts (CQ-2+CQ-8 dedup).
    {
      for (const { pattern, label } of FORBIDDEN_OPENER_PATTERNS) {
        if (pattern.test(explanation)) {
          issues.push(
            coach(
              'content',
              STAGE,
              'CONTENT_FORBIDDEN_OPENER',
              `Explanation starts with forbidden opener "${label}" — rewrite to integrate naturally`,
              {
                field: 'feedback.explanationText',
                actual: label,
                questionId: qId,
                familyId: fId,
                severity: 40,
                suggestion: `Remove the "${label}" opener and integrate the content naturally into the explanation`,
              },
            ),
          )
        }
      }
    }

    // Didactic depth check (only if not generic)
    // TOPO EXEMPTION: Topo is knowledge-recall. If the explanation references
    // geographic facts (hasModuleSpecificDepth), that IS didactic for Topo.
    // Requiring math-style "steps" or "reasoning" produces false positives.
    if (
      !isGenericText(explanation, GENERIC_EXPLANATION_PATTERNS) &&
      explanation.length >= 30
    ) {
      const isTopoWithGeographicDepth =
        module === 'Topo' &&
        /provincie|hoofdstad|rivier|land|continent|oceaan|gebergte|zee|grens|eiland|klimaat|berg|dal|kust|noorden|zuiden|oosten|westen/i.test(
          explanation,
        )
      if (!isDidactic(explanation) && !isTopoWithGeographicDepth) {
        issues.push(
          coach(
            'content',
            STAGE,
            'CONTENT_EXPLANATION_NOT_DIDACTIC',
            'Explanation lacks didactic depth — should show steps, reference rules, or explain reasoning',
            {
              field: 'feedback.explanationText',
              questionId: qId,
              familyId: fId,
              severity: 40,
            },
          ),
        )
      }
    }

    // ── NEW: Jargon detection (CONTENT_GOVERNANCE_FRAMEWORK §1.1) ────────
    // ENHANCED: Tier-aware jargon detection (GAP-FP1/FP2 fix)
    const jargonFound = detectJargon(explanation, tier)
    for (const { term, replacement } of jargonFound) {
      issues.push(
        coach(
          'content',
          STAGE,
          'CONTENT_JARGON_DETECTED',
          `Jargon "${term}" in explanation — too complex for children aged 9-11`,
          {
            field: 'feedback.explanationText',
            actual: term,
            questionId: qId,
            familyId: fId,
            severity: 35,
            suggestion: `Replace "${term}" with ${replacement}`,
          },
        ),
      )
    }

    // ── NEW: Passive voice detection (CONTENT_GOVERNANCE_FRAMEWORK §4.1) ─
    if (hasPassiveVoice(explanation)) {
      issues.push(
        info(
          'content',
          STAGE,
          'CONTENT_EXPLANATION_PASSIVE',
          'Explanation uses passive voice — use active voice ("kijk", "tel", "schrijf")',
          {
            field: 'feedback.explanationText',
            questionId: qId,
            familyId: fId,
            severity: 15,
            suggestion: 'Replace passive constructions with active verbs',
          },
        ),
      )
    }

    // ── NEW: Sentence length check (CONTENT_GOVERNANCE_FRAMEWORK §1.2, §4.1) ─
    const longSentences = detectLongSentences(explanation, tier)
    if (longSentences.length > 0) {
      const maxWords = getMaxWordsPerSentence(tier)
      issues.push(
        info(
          'content',
          STAGE,
          'CONTENT_SENTENCE_TOO_LONG',
          `${longSentences.length} sentence(s) exceed the ${maxWords}-word limit for tier ${tier}`,
          {
            field: 'feedback.explanationText',
            questionId: qId,
            familyId: fId,
            severity: 20,
            suggestion: `Keep sentences ≤${maxWords} words for tier ${tier} (groep ${tier <= 4 ? 6 : tier <= 8 ? 7 : 8})`,
          },
        ),
      )
    }

    // Module-specific rule reference
    if (module && requiresRuleReference(module)) {
      // Check if explanation references a rule (module-specific patterns)
      const pattern = getDistractorPattern(module)
      if (pattern && !pattern.test(explanation) && !isDidactic(explanation)) {
        issues.push(
          info(
            'content',
            STAGE,
            'CONTENT_MISSING_RULE_REF',
            `Explanation should reference module-specific rules for ${module}`,
            {
              field: 'feedback.explanationText',
              questionId: qId,
              familyId: fId,
              severity: 20,
            },
          ),
        )
      }
    }

    // Check strategy requirement (SWW tier 7+)
    if (module) {
      const checkMinTier = getCheckStrategyMinTier(module)
      if (checkMinTier !== null && tier >= checkMinTier) {
        // v5.11: EXPANDED — Recognize more Dutch pedagogical check strategies.
        // SWW explanations use various ways to describe verification:
        // - Direct form references: "ik-vorm", "hij-vorm"
        // - Infinitive checks: "infinitief", "hele werkwoord"
        // - Stem analysis: "stam + t", "stam+t"
        // - Tense verification: "verleden tijd", "tegenwoordige tijd"
        // - General check verbs: "controleer", "check"
        // - Substitution: "vul in", "probeer de zin"
        const CHECK_STRATEGY =
          /ik-vorm|hij-vorm|langer maken|worden|ik loop|hij loopt|zet.*om|controleer|check\b|infinitief|hele werkwoord|stam\s*\+?\s*t\b|verleden tijd|tegenwoordige tijd|voltooid deelwoord|vul.*in|probeer.*zin|enkelvoud|meervoud/i
        if (!CHECK_STRATEGY.test(explanation)) {
          issues.push(
            coach(
              'content',
              STAGE,
              'CONTENT_MISSING_CHECK_STRATEGY',
              `Explanation for ${module} tier ≥${checkMinTier} should include a check strategy`,
              {
                field: 'feedback.explanationText',
                questionId: qId,
                familyId: fId,
                severity: 40,
              },
            ),
          )
        }
      }
    }
  }

  // ── Distractor explanations ──────────────────────────────────────────────

  if (question.questionType === 'multiple_choice' && question.options) {
    const feedbackMap = question.feedback?.distractorExplanations ?? {}
    const optionCount = question.options.length

    // Check all options have explanations
    const policy = getPolicy()
    if (policy.featureFlags.enforceDistractorExplanations) {
      for (let i = 0; i < optionCount; i++) {
        const exp = feedbackMap[String(i)]
        if (!exp || typeof exp !== 'string' || exp.trim() === '') {
          issues.push(
            coach(
              'content',
              STAGE,
              'CONTENT_MISSING_DISTRACTOR_EXPLANATION',
              `Missing distractor explanation for option ${i}`,
              {
                field: `feedback.distractorExplanations.${i}`,
                questionId: qId,
                familyId: fId,
                severity: 45,
              },
            ),
          )
        }
      }
    }

    // Check distractor quality
    const minDistractor = getMinDistractorLength(tier)
    for (const [idx, exp] of Object.entries(feedbackMap)) {
      if (typeof exp !== 'string' || exp.trim() === '') continue

      // v5.10: Skip generic/misconception checks for the CORRECT answer.
      // The correct answer explanation (e.g., "Goed! Het honderdstencijfer is 7...")
      // is a confirmation, not a distractor. It naturally starts with affirmative
      // language that triggers false positives on generic distractor patterns.
      const isCorrectAnswer = Number(idx) === question.correctAnswer

      // Length check (applies to all, including correct answer)
      if (exp.trim().length < minDistractor) {
        issues.push(
          info(
            'content',
            STAGE,
            'CONTENT_DISTRACTOR_TOO_SHORT',
            `Distractor explanation for option ${idx} (${exp.trim().length} chars) below minimum ${minDistractor}`,
            {
              field: `feedback.distractorExplanations.${idx}`,
              questionId: qId,
              familyId: fId,
              severity: 15,
            },
          ),
        )
      }

      // Forbidden opener check on distractors (CAL-006 fix)
      // Uses shared FORBIDDEN_OPENER_PATTERNS from governance/utils.ts (CQ-2+CQ-8 dedup).
      if (!isCorrectAnswer) {
        for (const { pattern, label } of FORBIDDEN_OPENER_PATTERNS) {
          if (pattern.test(exp.trim())) {
            issues.push(
              coach(
                'content',
                STAGE,
                'CONTENT_FORBIDDEN_OPENER',
                `Distractor explanation for option ${idx} starts with forbidden opener "${label}"`,
                {
                  field: `feedback.distractorExplanations.${idx}`,
                  actual: label,
                  questionId: qId,
                  familyId: fId,
                  severity: 40,
                  suggestion: `Remove the "${label}" opener — explain the misconception directly`,
                },
              ),
            )
          }
        }
      }

      // Generic check — skip for correct answer
      if (!isCorrectAnswer && isGenericText(exp, GENERIC_DISTRACTOR_PATTERNS)) {
        issues.push(
          coach(
            'content',
            STAGE,
            'CONTENT_GENERIC_DISTRACTOR',
            `Distractor explanation for option ${idx} is generic — must name the specific misconception`,
            {
              field: `feedback.distractorExplanations.${idx}`,
              questionId: qId,
              familyId: fId,
              severity: 45,
              suggestion:
                'Explain WHY this answer is wrong and what misconception it represents',
            },
          ),
        )
      }

      // ── NEW: Jargon check on distractor text (CONTENT_GOVERNANCE_FRAMEWORK §1.1) ─
      // ENHANCED: Tier-aware jargon detection (GAP-FP1/FP2 fix)
      const distractorJargon = detectJargon(exp, tier)
      for (const { term, replacement } of distractorJargon) {
        issues.push(
          info(
            'content',
            STAGE,
            'CONTENT_JARGON_DETECTED',
            `Jargon "${term}" in distractor explanation for option ${idx}`,
            {
              field: `feedback.distractorExplanations.${idx}`,
              actual: term,
              questionId: qId,
              familyId: fId,
              severity: 30,
              suggestion: `Replace "${term}" with ${replacement}`,
            },
          ),
        )
      }
    }

    // Misconception reference check
    // v5.10: REFINED — accept substantive misconception-addressing patterns
    // in addition to module-specific keyword matches. The "Je dacht waarschijnlijk"
    // pattern is the standard Dutch pedagogical way to address misconceptions.
    if (module) {
      const misconceptionPattern = getDistractorPattern(module)
      if (misconceptionPattern) {
        const wrongEntries = Object.entries(feedbackMap).filter(
          ([idx]) => Number(idx) !== question.correctAnswer,
        )
        if (wrongEntries.length > 0) {
          const hasMisconceptionRef = wrongEntries.some(
            ([, exp]) =>
              typeof exp === 'string' &&
              (misconceptionPattern.test(exp) ||
                isDidactic(exp) ||
                // v5.10: Accept explicit misconception-addressing patterns
                /je dacht (waarschijnlijk|misschien)/i.test(exp) ||
                // Accept substantive explanations (>80 chars, not generic)
                (exp.trim().length >= 80 &&
                  !isGenericText(exp, GENERIC_DISTRACTOR_PATTERNS))),
          )
          if (!hasMisconceptionRef) {
            issues.push(
              coach(
                'content',
                STAGE,
                'CONTENT_NO_MISCONCEPTION_REF',
                `No distractor explanation references a ${module}-specific misconception`,
                {
                  field: 'feedback.distractorExplanations',
                  questionId: qId,
                  familyId: fId,
                  severity: 40,
                },
              ),
            )
          }
        }
      }
    }
  }

  // ── Notation enforcement ─────────────────────────────────────────────────

  if (module) {
    const notation = getNotationRequirements(module)
    const fullText =
      (question.question ?? '') + ' ' + (question.options?.join(' ') ?? '')

    // LaTeX fractions
    if (
      notation.requireLatexFractions &&
      isPolicyFeatureEnabled('enforceLatex')
    ) {
      const isFractionContext =
        fId.includes('BREUK') ||
        (question.skill ?? '').toLowerCase().includes('breuk')
      if (
        isFractionContext &&
        fullText.includes('/') &&
        !fullText.includes('\\frac')
      ) {
        issues.push(
          coach(
            'content',
            STAGE,
            'CONTENT_MISSING_LATEX',
            'Fractions should use LaTeX (\\frac{}{}) instead of /',
            {
              field: 'question',
              questionId: qId,
              familyId: fId,
              severity: 35,
            },
          ),
        )
      }
    }

    // Comma decimals
    if (notation.requireCommaDecimals) {
      // v5.11: FIXED — Exclude Dutch thousand-separator dots.
      // Dutch uses dots for thousands (3.427 = drieduizend vierhonderdzevenentwintig)
      // and commas for decimals (3,5 = drie komma vijf).
      // A thousand separator: digit(s) followed by dot followed by exactly 3 digits.
      // A decimal dot: digit(s) followed by dot followed by 1-2 or 4+ digits.
      // Strip thousand separators before checking for decimal dots.
      const textForDotCheck = fullText.replace(/\d\.\d{3}(?!\d)/g, (match) =>
        match.replace('.', ''),
      )
      if (
        textForDotCheck.match(/\d+\.\d+/) &&
        !fId.includes('WETENSCHAPPELIJK')
      ) {
        issues.push(
          coach(
            'content',
            STAGE,
            'CONTENT_DOT_DECIMAL',
            'Use comma (,) for decimals, not dot (.)',
            {
              field: 'question',
              questionId: qId,
              familyId: fId,
              severity: 40,
            },
          ),
        )
      }
    }

    // Euro comma
    if (notation.requireEuroComma) {
      if (fullText.includes('€') && fullText.match(/\d+\.\d{2}/)) {
        issues.push(
          coach(
            'content',
            STAGE,
            'CONTENT_EURO_DOT',
            'Use comma (€3,50) not dot (€3.50) for euro notation',
            {
              field: 'question',
              questionId: qId,
              familyId: fId,
              severity: 40,
            },
          ),
        )
      }
    }

    // Superscript units
    if (
      notation.requireSuperscriptUnits &&
      isPolicyFeatureEnabled('enforceUnitNotation')
    ) {
      const requiresSuperscript = fId.includes('OPP') || fId.includes('INH')
      if (
        requiresSuperscript &&
        (fullText.includes('m2') ||
          fullText.includes('cm2') ||
          fullText.includes('m3'))
      ) {
        issues.push(
          coach(
            'content',
            STAGE,
            'CONTENT_MISSING_SUPERSCRIPT',
            'Use superscripts (m², cm³) instead of m2, cm3',
            {
              field: 'question',
              questionId: qId,
              familyId: fId,
              severity: 35,
            },
          ),
        )
      }
    }
  }

  // ── GAP-P1: solutionSteps check for ALL tiers ───────────────────────────
  // Previously only enforced for plus-level tiers. Now emits info for any
  // question missing solutionSteps (catches SCAN-033: TGL-TIJD-ANALOOG).

  if (!isPlusTier(tier)) {
    const steps = question.feedback?.solutionSteps
    if (!steps || !Array.isArray(steps) || steps.length === 0) {
      issues.push(
        info(
          'content',
          STAGE,
          'CONTENT_MISSING_SOLUTION_STEPS',
          `Question is missing feedback.solutionSteps — recommended for all tiers`,
          {
            field: 'feedback.solutionSteps',
            questionId: qId,
            familyId: fId,
            severity: 15,
            suggestion:
              'Add step-by-step solution explanation to improve didactic quality',
          },
        ),
      )
    }
  }

  // ── GAP-P2: citoDifficulty / citoDiscrimination presence check ──────────
  // Previously only checked citoDifficulty minimum for plus tiers.
  // Now checks presence for all questions (catches SCAN-034).

  {
    const hasCitoDiff =
      question.citoDifficulty !== undefined && question.citoDifficulty !== null
    const hasCitoDisc =
      (question as Record<string, unknown>).citoDiscrimination !== undefined &&
      (question as Record<string, unknown>).citoDiscrimination !== null
    const isAssessmentType = question.questionType
      ? isAssessmentEligibleType(question.questionType)
      : false

    if (!hasCitoDiff) {
      issues.push(
        isAssessmentType
          ? coach(
              'content',
              STAGE,
              'CONTENT_MISSING_CITO_DIFFICULTY',
              `Assessment-eligible question is missing citoDifficulty parameter`,
              {
                field: 'citoDifficulty',
                questionId: qId,
                familyId: fId,
                severity: 35,
                suggestion:
                  'Add citoDifficulty (0.0-1.0) for calibration and assessment selection',
              },
            )
          : info(
              'content',
              STAGE,
              'CONTENT_MISSING_CITO_DIFFICULTY',
              `Question is missing citoDifficulty parameter`,
              {
                field: 'citoDifficulty',
                questionId: qId,
                familyId: fId,
                severity: 10,
                suggestion: 'Add citoDifficulty (0.0-1.0) for calibration',
              },
            ),
      )
    }

    if (!hasCitoDisc) {
      issues.push(
        isAssessmentType
          ? coach(
              'content',
              STAGE,
              'CONTENT_MISSING_CITO_DISCRIMINATION',
              `Assessment-eligible question is missing citoDiscrimination parameter`,
              {
                field: 'citoDiscrimination',
                questionId: qId,
                familyId: fId,
                severity: 30,
                suggestion:
                  'Add citoDiscrimination (0.0-1.0) for assessment quality gating',
              },
            )
          : info(
              'content',
              STAGE,
              'CONTENT_MISSING_CITO_DISCRIMINATION',
              `Question is missing citoDiscrimination parameter`,
              {
                field: 'citoDiscrimination',
                questionId: qId,
                familyId: fId,
                severity: 10,
                suggestion: 'Add citoDiscrimination (0.0-1.0) for calibration',
              },
            ),
      )
    }
  }

  // ── NEW: citoDifficulty RANGE validation (D4: 0.15-0.75 per tier band) ──
  {
    const citoDiff = question.citoDifficulty
    if (citoDiff !== undefined && typeof citoDiff === 'number') {
      // Global range
      if (citoDiff < 0.1 || citoDiff > 0.85) {
        issues.push(
          coach(
            'content',
            STAGE,
            'CONTENT_CITO_DIFFICULTY_OUT_OF_RANGE',
            `citoDifficulty ${citoDiff} outside valid range [0.10, 0.85]`,
            {
              field: 'citoDifficulty',
              expected: '0.10-0.85',
              actual: String(citoDiff),
              questionId: qId,
              familyId: fId,
              severity: 40,
            },
          ),
        )
      }

      // Tier-band specific ranges (from SEED_REVIEW_CHECKLIST §D4)
      const band = getTierBand(tier)
      const bandRanges: Record<string, { min: number; max: number }> = {
        basic: { min: 0.15, max: 0.55 },
        intermediate: { min: 0.2, max: 0.6 },
        advanced: { min: 0.25, max: 0.7 },
        expert: { min: 0.3, max: 0.8 },
      }
      const bandRange = bandRanges[band]
      if (bandRange && (citoDiff < bandRange.min || citoDiff > bandRange.max)) {
        issues.push(
          info(
            'content',
            STAGE,
            'CONTENT_CITO_DIFFICULTY_BAND_MISMATCH',
            `citoDifficulty ${citoDiff} outside recommended range [${bandRange.min}, ${bandRange.max}] for ${band} tier band`,
            {
              field: 'citoDifficulty',
              expected: `${bandRange.min}-${bandRange.max}`,
              actual: String(citoDiff),
              questionId: qId,
              familyId: fId,
              severity: 20,
              suggestion: `Adjust citoDifficulty to fit the ${band} band range, or verify this difficulty is intentional`,
            },
          ),
        )
      }
    }
  }

  // ── NEW: citoDiscrimination RANGE validation (D4: 0.60-0.85) ────────────
  {
    const citoDisc = (question as Record<string, unknown>).citoDiscrimination
    if (citoDisc !== undefined && typeof citoDisc === 'number') {
      if (citoDisc < 0.4 || citoDisc > 0.95) {
        issues.push(
          coach(
            'content',
            STAGE,
            'CONTENT_CITO_DISCRIMINATION_OUT_OF_RANGE',
            `citoDiscrimination ${citoDisc} outside valid range [0.40, 0.95]`,
            {
              field: 'citoDiscrimination',
              expected: '0.40-0.95',
              actual: String(citoDisc),
              questionId: qId,
              familyId: fId,
              severity: 35,
            },
          ),
        )
      } else if (citoDisc < 0.6 || citoDisc > 0.85) {
        issues.push(
          info(
            'content',
            STAGE,
            'CONTENT_CITO_DISCRIMINATION_BAND_MISMATCH',
            `citoDiscrimination ${citoDisc} outside recommended range [0.60, 0.85]`,
            {
              field: 'citoDiscrimination',
              expected: '0.60-0.85',
              actual: String(citoDisc),
              questionId: qId,
              familyId: fId,
              severity: 15,
              suggestion:
                'Adjust citoDiscrimination to 0.60-0.85 for optimal assessment quality',
            },
          ),
        )
      }
    }
  }

  // ── Tier-band cognitive level validation (CAL-003) ───────────────────────
  // Validates cognitive level appropriateness for ALL tier bands, not just plus.
  // - Basis (1-4): should be recall/comprehension — flag analysis as too high
  // - Eind/Advanced (9-12): should be application/analysis — flag recall as too low

  if (!isPlusTier(tier)) {
    const band = getTierBand(tier)
    const cogLevel = detectCognitiveLevel(question)

    if (band === 'basic' && cogLevel === 'analysis') {
      issues.push(
        coach(
          'content',
          STAGE,
          'CONTENT_BASIS_COGNITIVE_TOO_HIGH',
          `Basis-tier ${tier} question has "${cogLevel}" cognitive level — expected recall or comprehension for this tier band`,
          {
            field: 'question',
            expected: 'recall or comprehension',
            actual: cogLevel,
            questionId: qId,
            familyId: fId,
            severity: 40,
            suggestion:
              'Basis-tier questions (groep 6) should focus on recall and comprehension — save analysis-level questions for higher tiers',
          },
        ),
      )
    }

    // TOPO EXEMPTION: Topo is inherently recall-based even at advanced tiers.
    // Knowing world capitals or mountain ranges IS the skill — you can't make
    // "What is the capital of France?" into an "application" question.
    if (band === 'advanced' && cogLevel === 'recall' && module !== 'Topo') {
      issues.push(
        coach(
          'content',
          STAGE,
          'CONTENT_EIND_COGNITIVE_TOO_LOW',
          `Eind-tier ${tier} question has only "${cogLevel}" cognitive level — expected application or analysis for this tier band`,
          {
            field: 'question',
            expected: 'application or analysis',
            actual: cogLevel,
            questionId: qId,
            familyId: fId,
            severity: 35,
            suggestion:
              'Eind-tier questions (groep 8) should require application or analysis — add calculation steps, comparisons, or reasoning to increase cognitive demand',
          },
        ),
      )
    }
  }

  // ── Plus-level enforcement (§3.3 Master Rulebook) ────────────────────────

  if (isPlusTier(tier)) {
    const plusReqs = getPlusRequirements(tier)
    if (plusReqs) {
      // 1. Cognitive level check
      const cogLevel = detectCognitiveLevel(question)
      const cogLevelOrder = {
        recall: 0,
        comprehension: 1,
        application: 2,
        analysis: 3,
      }
      const minCogOrder = cogLevelOrder[plusReqs.minCognitiveLevel]
      const actualCogOrder = cogLevelOrder[cogLevel]

      // TOPO EXEMPTION: Topo is inherently recall-based. Even plus-tier Topo
      // questions test broader/deeper geographic knowledge, not higher cognitive levels.
      if (actualCogOrder < minCogOrder && module !== 'Topo') {
        issues.push(
          coach(
            'content',
            STAGE,
            'CONTENT_PLUS_COGNITIVE_TOO_LOW',
            `Plus-level tier ${tier} requires ≥${plusReqs.minCognitiveLevel} cognitive level, detected "${cogLevel}"`,
            {
              field: 'question',
              expected: plusReqs.minCognitiveLevel,
              actual: cogLevel,
              questionId: qId,
              familyId: fId,
              severity: 55,
              suggestion: `Plus questions should require ${plusReqs.minCognitiveLevel}-level thinking: multi-step reasoning, transfer to new contexts, or combining skills — not just recall or simple comprehension`,
            },
          ),
        )
      }

      // 2. citoDifficulty minimum check
      const citoDiff = question.citoDifficulty
      if (citoDiff !== undefined && typeof citoDiff === 'number') {
        if (citoDiff < plusReqs.minCitoDifficulty) {
          issues.push(
            coach(
              'content',
              STAGE,
              'CONTENT_PLUS_DIFFICULTY_TOO_LOW',
              `Plus-level tier ${tier} requires citoDifficulty ≥${plusReqs.minCitoDifficulty}, got ${citoDiff}`,
              {
                field: 'citoDifficulty',
                expected: `≥${plusReqs.minCitoDifficulty}`,
                actual: String(citoDiff),
                questionId: qId,
                familyId: fId,
                severity: 40,
                suggestion: `Plus questions should be harder than base-level. Increase complexity: use larger numbers, multi-step problems, unfamiliar contexts, or combined skills`,
              },
            ),
          )
        }
      }

      // 3. solutionSteps requirement
      if (plusReqs.requireSolutionSteps) {
        const steps = question.feedback?.solutionSteps
        if (!steps || !Array.isArray(steps) || steps.length === 0) {
          issues.push(
            coach(
              'content',
              STAGE,
              'CONTENT_PLUS_MISSING_STEPS',
              `Plus-level tier ${tier} requires solutionSteps in feedback`,
              {
                field: 'feedback.solutionSteps',
                questionId: qId,
                familyId: fId,
                severity: 45,
                suggestion:
                  'Plus questions must have step-by-step solution explanations to support the higher cognitive demand',
              },
            ),
          )
        } else if (steps.length < plusReqs.minSolutionSteps) {
          issues.push(
            info(
              'content',
              STAGE,
              'CONTENT_PLUS_FEW_STEPS',
              `Plus-level tier ${tier} recommends ≥${plusReqs.minSolutionSteps} solution steps, got ${steps.length}`,
              {
                field: 'feedback.solutionSteps',
                expected: `≥${plusReqs.minSolutionSteps}`,
                actual: String(steps.length),
                questionId: qId,
                familyId: fId,
                severity: 25,
              },
            ),
          )
        }
      }

      // 4. Multi-step reasoning indicator
      if (plusReqs.requireMultiStep) {
        const qText = question.question ?? ''
        const MULTI_STEP_INDICATORS =
          /eerst.*dan|stap.*stap|vervolgens|daarna|combineer|vergelijk.*en|bereken.*en.*bereken|twee.*stappen|meerdere/i
        const HAS_MULTI_OPERATION = /\d+.*[+\-×÷].*\d+.*[+\-×÷].*\d+/
        const isMultiStep =
          MULTI_STEP_INDICATORS.test(qText) ||
          HAS_MULTI_OPERATION.test(qText) ||
          (question.feedback?.solutionSteps?.length ?? 0) >= 3

        if (!isMultiStep) {
          issues.push(
            info(
              'content',
              STAGE,
              'CONTENT_PLUS_SINGLE_STEP',
              `Plus-level tier ${tier} expects multi-step or transfer questions, but this appears to be single-step`,
              {
                field: 'question',
                questionId: qId,
                familyId: fId,
                severity: 30,
                suggestion:
                  'Plus questions should involve: multi-step reasoning, transfer to unfamiliar contexts, combining multiple skills, or analysis/comparison — not just a harder version of the same procedure',
              },
            ),
          )
        }
      }
    }
  }

  return issues
}
